import React, { useState } from 'react';

function SimproTimeCalculator() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [quoteId, setQuoteId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const APP_PASSWORD = 'FibreNet2024!Secure';
  const API_URL = 'https://simpro-backend.vercel.app';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === APP_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid password');
    }
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const url = `${API_URL}/api/calculate?quoteId=${quoteId}&token=${encodeURIComponent(APP_PASSWORD)}`;
      
      const response = await fetch(url, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate time');
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div style={{
        fontFamily: "'Raleway', sans-serif",
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          padding: '2rem',
          width: '100%',
          maxWidth: '28rem'
        }}>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: '1.5rem',
            color: '#1f2937'
          }}>
            🔒 Simpro Time Calculator
          </h1>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter password"
              />
            </div>
            {error && (
              <div style={{
                color: '#dc2626',
                fontSize: '0.875rem',
                textAlign: 'center',
                marginBottom: '1rem'
              }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              style={{
                width: '100%',
                background: '#4f46e5',
                color: 'white',
                padding: '0.5rem',
                borderRadius: '0.5rem',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main App Screen
  return (
    <div style={{
      fontFamily: "'Raleway', sans-serif",
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
        <div style={{
          background: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          padding: '2rem'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem'
          }}>
            <h1 style={{
              fontSize: '1.875rem',
              fontWeight: 'bold',
              color: '#1f2937',
              margin: 0
            }}>
              🕐 Simpro Time Calculator
            </h1>
            <button
              onClick={() => {
                setIsAuthenticated(false);
                setResult(null);
                setError('');
                setQuoteId('');
              }}
              style={{
                fontSize: '0.875rem',
                color: '#4b5563',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Logout
            </button>
          </div>

          {/* Input Form */}
          <form onSubmit={handleCalculate} style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={quoteId}
                onChange={(e) => setQuoteId(e.target.value)}
                placeholder="Enter Quote ID (e.g., 123)"
                style={{
                  flex: '1 1 300px',
                  padding: '0.75rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '1.125rem',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="submit"
                disabled={loading || !quoteId}
                style={{
                  background: (loading || !quoteId) ? '#9ca3af' : '#4f46e5',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: (loading || !quoteId) ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {loading ? '⏳ Calculating...' : '🔍 Calculate'}
              </button>
            </div>
          </form>

          {/* Error Message */}
          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '1rem',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem'
            }}>
              {error}
            </div>
          )}

          {/* Results */}
          {result && (
            <div>
              {/* Quote Info */}
              <div style={{
                background: '#eef2ff',
                border: '1px solid #c7d2fe',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginTop: 0,
                  marginBottom: '0.25rem'
                }}>
                  Quote #{result.quoteId}
                </h2>
                <p style={{ color: '#4b5563', margin: 0 }}>{result.quoteName}</p>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  marginTop: '0.25rem',
                  marginBottom: 0
                }}>
                  {result.sectionsProcessed} sections • {result.billable.itemsWithTimes} billable items
                </p>
              </div>

              {/* BILLABLE SECTION */}
              <div style={{ marginBottom: '2rem' }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginTop: 0,
                  marginBottom: '1rem',
                  borderBottom: '2px solid #e5e7eb',
                  paddingBottom: '0.5rem'
                }}>
                  Billable Time
                </h2>

                {/* Time Cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  {/* Commissioning */}
                  <div style={{
                    background: '#eff6ff',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    border: '1px solid #bfdbfe'
                  }}>
                    <h3 style={{
                      fontWeight: '600',
                      color: '#1e3a8a',
                      marginBottom: '0.5rem',
                      marginTop: 0,
                      fontSize: '0.875rem'
                    }}>
                      Commissioning Time
                    </h3>
                    <p style={{
                      fontSize: '1.875rem',
                      fontWeight: 'bold',
                      color: '#1e40af',
                      margin: 0
                    }}>
                      {result.billable.totals.commissioningHours} hrs
                    </p>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#2563eb',
                      marginTop: '0.25rem',
                      marginBottom: 0
                    }}>
                      ({result.billable.totals.commissioningMinutes} min)
                    </p>
                  </div>

                  {/* Testing */}
                  <div style={{
                    background: '#f0fdf4',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    border: '1px solid #bbf7d0'
                  }}>
                    <h3 style={{
                      fontWeight: '600',
                      color: '#14532d',
                      marginBottom: '0.5rem',
                      marginTop: 0,
                      fontSize: '0.875rem'
                    }}>
                      Testing Time
                    </h3>
                    <p style={{
                      fontSize: '1.875rem',
                      fontWeight: 'bold',
                      color: '#15803d',
                      margin: 0
                    }}>
                      {result.billable.totals.testingHours} hrs
                    </p>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#16a34a',
                      marginTop: '0.25rem',
                      marginBottom: 0
                    }}>
                      ({result.billable.totals.testingMinutes} min)
                    </p>
                  </div>

                  {/* Design */}
                  <div style={{
                    background: '#faf5ff',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    border: '1px solid #e9d5ff'
                  }}>
                    <h3 style={{
                      fontWeight: '600',
                      color: '#581c87',
                      marginBottom: '0.5rem',
                      marginTop: 0,
                      fontSize: '0.875rem'
                    }}>
                      Design Time
                    </h3>
                    <p style={{
                      fontSize: '1.875rem',
                      fontWeight: 'bold',
                      color: '#7c3aed',
                      margin: 0
                    }}>
                      {result.billable.totals.designHours} hrs
                    </p>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#9333ea',
                      marginTop: '0.25rem',
                      marginBottom: 0
                    }}>
                      ({result.billable.totals.designMinutes} min)
                    </p>
                  </div>

                  {/* Total */}
                  <div style={{
                    background: '#e0e7ff',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    border: '2px solid #a5b4fc'
                  }}>
                    <h3 style={{
                      fontWeight: '600',
                      color: '#312e81',
                      marginBottom: '0.5rem',
                      marginTop: 0,
                      fontSize: '0.875rem'
                    }}>
                      Total Time
                    </h3>
                    <p style={{
                      fontSize: '1.875rem',
                      fontWeight: 'bold',
                      color: '#4338ca',
                      margin: 0
                    }}>
                      {result.billable.totals.totalHours} hrs
                    </p>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#4f46e5',
                      marginTop: '0.25rem',
                      marginBottom: 0
                    }}>
                      ({result.billable.totals.totalMinutes} min)
                    </p>
                  </div>
                </div>

                {/* Billable Item Breakdown Table */}
                {result.billable.items && result.billable.items.length > 0 && (
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    border: '1px solid #e5e7eb',
                    overflowX: 'auto'
                  }}>
                    <h3 style={{
                      fontWeight: '600',
                      color: '#1f2937',
                      marginTop: 0,
                      marginBottom: '0.75rem'
                    }}>
                      Item Breakdown
                    </h3>
                    <table style={{
                      width: '100%',
                      fontSize: '0.875rem',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: '600' }}>Section</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: '600' }}>Item</th>
                          <th style={{ textAlign: 'center', padding: '0.5rem', fontWeight: '600' }}>Qty</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600' }}>Comm</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600' }}>Test</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600' }}>Design</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.billable.items.map((item, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>{item.section}</td>
                            <td style={{ padding: '0.5rem' }}>{item.name}</td>
                            <td style={{ textAlign: 'center', padding: '0.5rem' }}>{item.quantity}</td>
                            <td style={{ textAlign: 'right', padding: '0.5rem' }}>{item.commissioning}m</td>
                            <td style={{ textAlign: 'right', padding: '0.5rem' }}>{item.testing}m</td>
                            <td style={{ textAlign: 'right', padding: '0.5rem' }}>{item.design}m</td>
                            <td style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600' }}>{item.totalMinutes}m</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* OPTIONAL SECTION */}
              {result.optional && result.optional.length > 0 && (
                <div>
                  <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginTop: 0,
                    marginBottom: '1rem',
                    borderBottom: '2px solid #e5e7eb',
                    paddingBottom: '0.5rem'
                  }}>
                    Optional (Non-Billable) Time
                  </h2>

                  {result.optional.map((section, sectionIdx) => (
                    <div key={sectionIdx} style={{ marginBottom: '2rem' }}>
                      {/* Section Header */}
                      <div style={{
                        background: '#fef3c7',
                        border: '2px solid #fbbf24',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}>
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: '600',
                          color: '#92400e',
                          marginTop: 0,
                          marginBottom: '0.5rem'
                        }}>
                          {section.sectionName}
                        </h3>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: '0.5rem',
                          fontSize: '0.875rem',
                          color: '#78350f'
                        }}>
                          <div>
                            <strong>Commissioning:</strong> {(section.totals.commissioning / 60).toFixed(2)}h ({section.totals.commissioning}m)
                          </div>
                          <div>
                            <strong>Testing:</strong> {(section.totals.testing / 60).toFixed(2)}h ({section.totals.testing}m)
                          </div>
                          <div>
                            <strong>Design:</strong> {(section.totals.design / 60).toFixed(2)}h ({section.totals.design}m)
                          </div>
                          <div>
                            <strong>Total:</strong> {(section.totals.total / 60).toFixed(2)}h ({section.totals.total}m)
                          </div>
                        </div>
                      </div>

                      {/* Cost Centers in this Section */}
                      {section.costCenters.map((costCenter, ccIdx) => (
                        <div key={ccIdx} style={{
                          background: '#fffbeb',
                          border: '1px solid #fde68a',
                          borderRadius: '0.5rem',
                          padding: '1rem',
                          marginBottom: '1rem',
                          marginLeft: '1rem'
                        }}>
                          <h4 style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#92400e',
                            marginTop: 0,
                            marginBottom: '0.75rem'
                          }}>
                            {costCenter.name}
                          </h4>

                          {/* Items Table */}
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{
                              width: '100%',
                              fontSize: '0.875rem',
                              borderCollapse: 'collapse'
                            }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #fbbf24' }}>
                                  <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: '600', color: '#92400e' }}>Item</th>
                                  <th style={{ textAlign: 'center', padding: '0.5rem', fontWeight: '600', color: '#92400e' }}>Qty</th>
                                  <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600', color: '#92400e' }}>Comm</th>
                                  <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600', color: '#92400e' }}>Test</th>
                                  <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600', color: '#92400e' }}>Design</th>
                                  <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600', color: '#92400e' }}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {costCenter.items.map((item, itemIdx) => (
                                  <tr key={itemIdx} style={{ borderBottom: '1px solid #fde68a' }}>
                                    <td style={{ padding: '0.5rem', color: '#78350f' }}>{item.name}</td>
                                    <td style={{ textAlign: 'center', padding: '0.5rem', color: '#78350f' }}>{item.quantity}</td>
                                    <td style={{ textAlign: 'right', padding: '0.5rem', color: '#78350f' }}>{item.commissioning}m</td>
                                    <td style={{ textAlign: 'right', padding: '0.5rem', color: '#78350f' }}>{item.testing}m</td>
                                    <td style={{ textAlign: 'right', padding: '0.5rem', color: '#78350f' }}>{item.design}m</td>
                                    <td style={{ textAlign: 'right', padding: '0.5rem', fontWeight: '600', color: '#78350f' }}>{item.totalMinutes}m</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Cost Center Totals */}
                          <div style={{
                            marginTop: '0.75rem',
                            paddingTop: '0.75rem',
                            borderTop: '1px solid #fbbf24',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: '#92400e'
                          }}>
                            <div>Total: {(costCenter.totals.total / 60).toFixed(2)}h ({costCenter.totals.total}m)</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SimproTimeCalculator;